import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import templatesAdminService, {
  type CampoConfig,
} from "../../../services/templatesAdminService";
import type { ConfigOrcamento } from "../../../types/orcamento";
import ConfiguracaoOrcamento from "./ConfiguracaoOrcamento";

interface Campo extends CampoConfig {
  id: string;
  ordem?: number;
  config?: ConfigOrcamento; // Para tipo orcamento
}

export default function FormTemplate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdicao = !!id;

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [campos, setCampos] = useState<Campo[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  
  // Configurações padrão
  const [limiteInscricoes, setLimiteInscricoes] = useState<number | string>("");
  const [notaCorte, setNotaCorte] = useState<number | string>(7.0);
  const [quantidadeRelatorios, setQuantidadeRelatorios] = useState<number | string>(4);

  useEffect(() => {
    if (isEdicao) {
      const carregarTemplate = async () => {
        try {
          setLoading(true);
          const response = await templatesAdminService.buscar(parseInt(id));
          setNome(response.template.nome);
          setDescricao(response.template.descricao);
          
          // Carregar configurações padrão
          const config = response.template.configuracoes_padrao as any || {};
          setLimiteInscricoes(config.limite_inscricoes_por_proponente ?? "");
          setNotaCorte(config.nota_corte ?? 7.0);
          setQuantidadeRelatorios(config.quantidade_relatorios ?? 4);

          // Converter schema_campos para array e ordenar
          const camposArray: Campo[] = Object.entries(
            response.template.schema_campos || {}
          ).map(([key, config], index) => ({
            id: key,
            ...config,
            // Se não tem ordem definida, usa a ordem do índice
            ordem:
              (config as Campo).ordem !== undefined
                ? (config as Campo).ordem
                : index,
          }));

          // Ordenar campos pela propriedade ordem
          camposArray.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

          setCampos(camposArray);
        } catch (err) {
          console.error("Erro ao carregar template:", err);
          toast.error("Erro ao carregar template");
          navigate("/admin/templates");
        } finally {
          setLoading(false);
        }
      };

      void carregarTemplate();
    }
  }, [id, isEdicao, navigate]);

  const adicionarCampo = () => {
    const novoCampo: Campo = {
      id: `campo_${Date.now()}`,
      type: "text",
      label: "",
      required: false,
      placeholder: "",
      ordem: campos.length, // Define ordem como último campo
    };
    setCampos([...campos, novoCampo]);
  };

  // Gera ID automaticamente baseado no label
  const gerarIdDoLabel = (label: string): string => {
    return label
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove acentos
      .replace(/[^\w\s]/gi, "") // Remove caracteres especiais
      .replace(/\s+/g, "_") // Substitui espaços por underscore
      .replace(/_+/g, "_") // Remove underscores duplicados
      .replace(/^_|_$/g, ""); // Remove underscores no início/fim
  };

  const removerCampo = (index: number) => {
    const novosCampos = campos.filter((_, i) => i !== index);
    // Atualizar ordem dos campos restantes
    novosCampos.forEach((campo, i) => {
      campo.ordem = i;
    });
    setCampos(novosCampos);
  };

  const moverCampo = (index: number, direcao: "cima" | "baixo") => {
    const novoIndex = direcao === "cima" ? index - 1 : index + 1;

    if (novoIndex < 0 || novoIndex >= campos.length) return;

    const novosCampos = [...campos];
    [novosCampos[index], novosCampos[novoIndex]] = [
      novosCampos[novoIndex],
      novosCampos[index],
    ];

    // Atualizar ordem
    novosCampos.forEach((campo, i) => {
      campo.ordem = i;
    });

    setCampos(novosCampos);
  };

  const atualizarCampo = (index: number, campo: Partial<Campo>) => {
    const novosCampos = [...campos];

    // Se está atualizando o label, atualiza também o ID automaticamente
    if (campo.label !== undefined) {
      let novoId = gerarIdDoLabel(campo.label);

      // Verificar se já existe um campo com esse ID (exceto o próprio)
      const idsExistentes = novosCampos
        .map((c, i) => (i !== index ? c.id : null))
        .filter(Boolean);

      // Se o ID já existe, adiciona sufixo numérico
      if (idsExistentes.includes(novoId)) {
        let contador = 1;
        let idComSufixo = `${novoId}_${contador}`;
        while (idsExistentes.includes(idComSufixo)) {
          contador++;
          idComSufixo = `${novoId}_${contador}`;
        }
        novoId = idComSufixo;
      }

      novosCampos[index] = { ...novosCampos[index], ...campo, id: novoId };
    } else {
      novosCampos[index] = { ...novosCampos[index], ...campo };
    }

    setCampos(novosCampos);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!nome.trim()) {
      toast.error("Nome do template é obrigatório");
      return;
    }

    if (campos.length === 0) {
      toast.error("Adicione pelo menos um campo ao template");
      return;
    }

    // Validar campos
    for (let i = 0; i < campos.length; i++) {
      const campo = campos[i];
      if (!campo.label.trim()) {
        toast.error(`Campo ${i + 1}: Label é obrigatório`);
        return;
      }
      if (!campo.id.trim()) {
        toast.error(`Campo ${i + 1}: ID é obrigatório`);
        return;
      }
      if (
        campo.type === "select" &&
        (!campo.options || campo.options.length === 0)
      ) {
        toast.error(
          `Campo ${i + 1}: Adicione pelo menos uma opção para o tipo 'select'`
        );
        return;
      }
    }

    // Converter array de campos para objeto, mantendo a ordem atual
    const schema_campos: Record<string, CampoConfig> = {};
    campos.forEach((campo, index) => {
      const { id: campoId, ...config } = campo;
      // Inclui ordem no config para preservar ordem ao recarregar
      schema_campos[campoId] = {
        ...config,
        ordem: index,
      } as CampoConfig;
    });

    // Montar configurações padrão
    const configuracoes_padrao: Record<string, unknown> = {
      nota_corte: Number(notaCorte) || 7.0,
      quantidade_relatorios: Number(quantidadeRelatorios) || 4,
    };
    
    // Só adiciona limite se foi definido
    if (limiteInscricoes !== "" && limiteInscricoes !== null) {
      configuracoes_padrao.limite_inscricoes_por_proponente = Number(limiteInscricoes);
    }

    try {
      setSalvando(true);

      if (isEdicao) {
        await templatesAdminService.atualizar(parseInt(id), {
          nome,
          descricao,
          schema_campos,
          configuracoes_padrao,
        });
        toast.success("Template atualizado com sucesso!");
      } else {
        await templatesAdminService.criar({
          nome,
          descricao,
          schema_campos,
          configuracoes_padrao,
        });
        toast.success("Template criado com sucesso!");
      }

      navigate("/admin/templates");
    } catch (err) {
      console.error("Erro ao salvar template:", err);
      toast.error("Erro ao salvar template");
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Botão Voltar */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/admin/templates")}
          className="text-gray-600 hover:text-gray-900 transition font-medium"
        >
          ← Voltar para Templates
        </button>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdicao ? "Editar Template" : "Novo Template"}
        </h1>
        <p className="mt-2 text-gray-600">
          {isEdicao
            ? "Atualize as informações do template"
            : "Crie um novo modelo de formulário para editais"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informações Básicas */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Informações Básicas
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Template <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Template para Projetos de Pesquisa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Descreva o propósito deste template..."
              />
            </div>
          </div>
        </div>

        {/* Configurações Padrão */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Configurações Padrão
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Limite de Inscrições por Proponente
              </label>
              <input
                type="number"
                min="1"
                value={limiteInscricoes}
                onChange={(e) => setLimiteInscricoes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Sem limite"
              />
              <p className="mt-1 text-xs text-gray-500">
                Deixe em branco para sem limite
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nota de Corte
              </label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={notaCorte}
                onChange={(e) => setNotaCorte(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantidade de Relatórios
              </label>
              <input
                type="number"
                min="0"
                value={quantidadeRelatorios}
                onChange={(e) => setQuantidadeRelatorios(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Campos do Formulário */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Campos do Formulário
            </h2>
            <button
              type="button"
              onClick={adicionarCampo}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
            >
              + Adicionar Campo
            </button>
          </div>

          {campos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum campo adicionado. Clique em "Adicionar Campo" para começar.
            </div>
          ) : (
            <div className="space-y-4">
              {campos.map((campo, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        Campo {index + 1}
                      </h3>
                      {/* Botões de ordenação */}
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moverCampo(index, "cima")}
                          disabled={index === 0}
                          className="p-1 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
                          title="Mover para cima"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => moverCampo(index, "baixo")}
                          disabled={index === campos.length - 1}
                          className="p-1 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
                          title="Mover para baixo"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removerCampo(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remover
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Label */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Label <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={campo.label}
                        onChange={(e) =>
                          atualizarCampo(index, { label: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Ex: Título do Projeto"
                      />
                    </div>

                    {/* Tipo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo
                      </label>
                      <select
                        value={campo.type}
                        onChange={(e) =>
                          atualizarCampo(index, {
                            type: e.target.value as CampoConfig["type"],
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="text">Texto</option>
                        <option value="textarea">Área de Texto</option>
                        <option value="number">Número</option>
                        <option value="currency">Moeda (R$)</option>
                        <option value="select">Seleção (Dropdown)</option>
                        <option value="date">Data</option>
                        <option value="url">URL</option>
                        <option value="orcamento">Orçamento (Planilha)</option>
                      </select>
                    </div>

                    {/* Placeholder */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Placeholder
                      </label>
                      <input
                        type="text"
                        value={campo.placeholder || ""}
                        onChange={(e) =>
                          atualizarCampo(index, { placeholder: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Ex: Digite o título..."
                      />
                    </div>

                    {/* Obrigatório */}
                    <div className="flex items-center">
                      <input
                        id={`campo-required-${index}`}
                        type="checkbox"
                        checked={campo.required || false}
                        onChange={(e) =>
                          atualizarCampo(index, { required: e.target.checked })
                        }
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label 
                        htmlFor={`campo-required-${index}`}
                        className="ml-2 text-sm text-gray-700 cursor-pointer"
                      >
                        Campo obrigatório
                      </label>
                    </div>

                    {/* Max Length (para text/textarea) */}
                    {(campo.type === "text" || campo.type === "textarea") && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tamanho Máximo
                        </label>
                        <input
                          type="number"
                          value={campo.maxLength || ""}
                          onChange={(e) =>
                            atualizarCampo(index, {
                              maxLength: parseInt(e.target.value) || undefined,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Ex: 200"
                        />
                      </div>
                    )}

                    {/* Min/Max (para number/currency) */}
                    {(campo.type === "number" || campo.type === "currency") && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor Mínimo
                          </label>
                          <input
                            type="number"
                            value={campo.min !== undefined ? campo.min : ""}
                            onChange={(e) =>
                              atualizarCampo(index, {
                                min:
                                  e.target.value === ""
                                    ? undefined
                                    : parseFloat(e.target.value),
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Ex: 0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor Máximo
                          </label>
                          <input
                            type="number"
                            value={campo.max !== undefined ? campo.max : ""}
                            onChange={(e) =>
                              atualizarCampo(index, {
                                max:
                                  e.target.value === ""
                                    ? undefined
                                    : parseFloat(e.target.value),
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Ex: 100000"
                          />
                        </div>
                      </>
                    )}

                    {/* Opções (para select) */}
                    {campo.type === "select" && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Opções (uma por linha){" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={(campo.options || []).join("\n")}
                          onChange={(e) => {
                            // Mantém todas as linhas durante a digitação
                            atualizarCampo(index, {
                              options: e.target.value.split("\n"),
                            });
                          }}
                          onBlur={(e) => {
                            // Remove linhas vazias apenas ao sair do campo
                            const linhas = e.target.value
                              .split("\n")
                              .filter((linha) => linha.trim() !== "");
                            atualizarCampo(index, {
                              options: linhas,
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                            }
                          }}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                          placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Pressione Enter para adicionar uma nova linha
                        </p>
                      </div>
                    )}

                    {/* Configuração de Orçamento */}
                    {campo.type === "orcamento" && (
                      <ConfiguracaoOrcamento
                        config={campo.config}
                        onChange={(config) => atualizarCampo(index, { config })}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botões */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => navigate("/admin/templates")}
            className="bg-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-400 transition font-medium"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={salvando}
            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition disabled:opacity-50 font-medium"
          >
            {salvando
              ? "Salvando..."
              : isEdicao
              ? "Atualizar Template"
              : "Criar Template"}
          </button>
        </div>
      </form>
    </div>
  );
}
